import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import "./LetterToShareholdersPage.css";
import {
  getNewsroomShareholderLetterByLanguage,
  getPublicShareholderLetterByLanguage,
  translateShareholderLetterContent,
} from "../services/shareholderLetterService";

const titleWords = ["Letter", "to", "Shareholders"];

const LetterToShareholdersDetailPage = () => {
  const { language } = useParams();
  const location = useLocation();
  const [isTitleVisible, setIsTitleVisible] = useState(false);
  const [letterData, setLetterData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const isNewsroomSource = useMemo(
    () =>
      new URLSearchParams(location.search).get("source") === "newsroom" ||
      location.state?.source === "newsroom",
    [location.search, location.state]
  );
  const translateTarget = useMemo(
    () => new URLSearchParams(location.search).get("translate") || "",
    [location.search]
  );
  const currentDate = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric"
  }).format(new Date());
  const languageTagLabel =
    translateTarget === "french"
      ? "French"
      : translateTarget === "dutch"
        ? "Dutch"
        : "English";

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [language, location.search]);

  useEffect(() => {
    const revealTimer = window.setTimeout(() => {
      setIsTitleVisible(true);
    }, 80);

    const loadLetter = async () => {
      try {
        setIsLoading(true);
        setErrorMessage("");
        const res = isNewsroomSource
          ? await getNewsroomShareholderLetterByLanguage(language)
          : await getPublicShareholderLetterByLanguage(language);
        const baseLetter = res?.data ?? null;

        if (!baseLetter) {
          setLetterData(null);
          return;
        }

        if (translateTarget === "french" || translateTarget === "dutch") {
          const textParts = [
            baseLetter.languageLabel || "English",
            ...((Array.isArray(baseLetter.content) ? baseLetter.content : [])),
          ];
          const translatedRes = await translateShareholderLetterContent(
            textParts,
            translateTarget
          );
          const translatedTexts = Array.isArray(translatedRes?.data)
            ? translatedRes.data
            : [];

          setLetterData({
            ...baseLetter,
            languageLabel: translateTarget === "french" ? "French" : "Dutch",
            content: translatedTexts.slice(1),
          });
          return;
        }

        setLetterData(baseLetter);
      } catch (error) {
        console.error("Failed to load shareholder letter:", error);
        setLetterData(null);
        setErrorMessage(
          error.response?.data?.message || "Unable to load this shareholder letter."
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadLetter();

    return () => {
      window.clearTimeout(revealTimer);
    };
  }, [isNewsroomSource, language, translateTarget]);

  const publishedDate = letterData?.publishedAt
    ? new Intl.DateTimeFormat("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric"
      }).format(new Date(letterData.publishedAt))
    : currentDate;

  return (
    <main className="shareholders-page shareholders-detail-page">
      <section className="shareholders-hero">
        <h1 className="shareholders-title">
          <span
            className={`shareholders-title-text shareholders-title-text-stacked${isTitleVisible ? " is-visible" : ""}`}
            aria-label="Letter to shareholders"
          >
            <span className="shareholders-title-line">
              {titleWords.slice(0, 2).map((word, wordIndex) => {
                const letterOffset = titleWords
                  .slice(0, wordIndex)
                  .reduce((count, currentWord) => count + currentWord.length, 0);

                return (
                  <span key={word} className="shareholders-title-word">
                    {word.split("").map((letter, letterIndex) => (
                      <span
                        key={`${word}-${letterIndex}`}
                        className="shareholders-title-letter"
                        style={{ "--reveal-order": letterOffset + letterIndex + 1 }}
                      >
                        {letter}
                      </span>
                    ))}
                  </span>
                );
              })}
            </span>

            <span className="shareholders-title-line">
              {titleWords.slice(2).map((word, wordIndex) => {
                const letterOffset =
                  titleWords[0].length + titleWords[1].length + wordIndex;

                return (
                  <span key={word} className="shareholders-title-word">
                    {word.split("").map((letter, letterIndex) => (
                      <span
                        key={`${word}-${letterIndex}`}
                        className="shareholders-title-letter"
                        style={{ "--reveal-order": letterOffset + letterIndex + 1 }}
                      >
                        {letter}
                      </span>
                    ))}
                  </span>
                );
              })}
            </span>
          </span>
        </h1>
      </section>

      <section className="shareholders-content shareholders-detail-header">
        <div className="shareholders-detail-language-tag">
          <span>{languageTagLabel}</span>
        </div>
      </section>

      <section className="shareholders-detail-body">
        <div className="shareholders-detail-article">
          {isLoading ? (
            <p>Loading shareholder letter...</p>
          ) : errorMessage ? (
            <p>{errorMessage}</p>
          ) : letterData ? (
            <>
              <p className="shareholders-detail-body-date">{publishedDate}</p>
              <p className="shareholders-detail-section-label">
                {letterData.sectionLabel || "I."}
              </p>
              {(Array.isArray(letterData.content) ? letterData.content : []).map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </>
          ) : (
            <p>No shareholder letter found for this language.</p>
          )}
        </div>
      </section>
    </main>
  );
};

export default LetterToShareholdersDetailPage;
